import styled, { css } from 'styled-components'
import { tabletBreakpoint, desktopBreakpoint, wideScreenBreakpoint } from 'react-components'
import DynamicMarkdown from '../../app-components/dynamic-components/dynamic-markdown'

export const Section = styled.section(
    ({ theme }) => css`
        width: inherit;
        padding: ${theme.spacing.medium} 0 ${theme.spacing.xSmall};

        @media only screen and (min-width: ${desktopBreakpoint.min}px) {
            padding: ${theme.spacing.xSmall} ${theme.spacing.large} ${theme.spacing.medium};
        }

        @media only screen and (min-width: ${wideScreenBreakpoint.min}px) {
            padding: ${theme.spacing.xLarge} 4rem ${theme.spacing.medium};
        }
    `,
)

export const TitleContainer = styled.div(
    ({ theme }) => css`
        padding: ${theme.spacing.small} ${theme.spacing.medium} ${theme.spacing.xxSmall};

        @media only screen and (min-width: ${tabletBreakpoint.min}px) {
            padding: ${theme.spacing.small} ${theme.spacing.large} ${theme.spacing.xxSmall};
        }

        @media only screen and (min-width: ${desktopBreakpoint.min}px) {
            padding: ${theme.spacing.xSmall} 0 ${theme.spacing.xSmall};
        }
    `,
)

export const Title = styled.h2(
    ({ theme }) => css`
        font-family: ${theme.typography.font.family.lato};
        font-size: 1.25rem;
        font-weight: ${theme.typography.font.weight.bold};
        line-height: 1.625rem;
        color: #282828;

        @media only screen and (min-width: ${desktopBreakpoint.min}px) {
            font-size: ${theme.typography.font.size.xxLarge};
            line-height: 2.5rem;
        }
    `,
)

export const Subtitle = styled.p(
    ({ theme }) => css`
        font-family: ${theme.typography.font.family.lato};
        font-size: ${theme.typography.font.size.small};
        font-weight: ${theme.typography.font.weight.normal};
        line-height: 1.375rem;
        color: #6f6f6f;
        margin-top: ${theme.spacing.xxSmall};

        @media only screen and (min-width: ${desktopBreakpoint.min}px) {
            font-size: ${theme.typography.font.size.medium};
            line-height: ${theme.typography.font.size.xLarge};
        }
    `,
)

export const CtaContainer = styled.div(
    ({ theme }) => css`
        font-family: ${theme.typography.font.family.lato};
        display: flex;
        justify-content: flex-start;
        padding: 0 ${theme.spacing.medium};
        margin-top: ${theme.spacing.xLarge};
        width: 100%;

        @media only screen and (min-width: ${tabletBreakpoint.min}px) {
            width: 13.25rem;
            padding: 0 ${theme.spacing.large};
        }

        @media only screen and (min-width: ${desktopBreakpoint.min}px) {
            padding: 0;
        }
    `,
)

export const StyledGhostButton = styled.button<{ borderRadius?: string }>(
    ({ theme, borderRadius }) => css`
        color: ${theme.colors.tfwBlack87};
        background-color: ${theme.colors.white};
        padding: ${theme.spacing.xSmall} 1.25rem;
        border: 1px solid ${theme.colors.tfwBlack87};
        border-radius: ${borderRadius || '1rem'};
        width: 100%;
        cursor: pointer;
        transition:
            background-color 0.2s ease,
            border-color 0.2s ease;

        @media only screen and (min-width: ${tabletBreakpoint.min}px) {
            padding: ${theme.spacing.small} 1.25rem;
        }

        @media only screen and (min-width: ${desktopBreakpoint.min}px) {
            padding: ${theme.spacing.small} 3.563rem;
        }

        &:hover {
            background-color: ${theme.colors.tfwYellowHover};
            color: ${theme.colors.tfwBlack87};
            border-color: ${theme.colors.tfwYellowHover};
        }
    `,
)

export const CtaButtonText = styled.div(
    ({ theme }) => css`
        font-family: ${theme.typography.font.family.lato};
        font-weight: 600;
        font-size: 0.875rem;
        line-height: 1.5rem;
        width: 100%;
        text-align: center;

        @media only screen and (min-width: ${tabletBreakpoint.min}px) {
            font-size: ${theme.typography.font.size.medium};
        }
    `,
)

export const TabsContainer = styled.div`
    display: flex;
    gap: 1rem;
    margin-bottom: 1rem;
`

export const TabButton = styled.button<{ active: boolean }>`
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-weight: 400;
    font-size: 0.875rem;
    border: ${({ active, theme }) =>
        active ? `2px solid ${theme.colors.tfwBlack99}` : `1px solid ${theme.colors.tfwBlack10}`};
    cursor: pointer;
    background-color: ${({ active, theme }) => (active ? theme.colors.tfwBlackOpacity10 : theme.colors.white)};
    transition: all 0.2s ease;
    &:hover {
        opacity: 0.85;
    }
`

export const CustomMarkdown = styled(DynamicMarkdown)(
    ({ theme }) => css`
        & ul,
        li {
            font: ${theme.typography.font.lato.weight.regular} ${theme.typography.font.size.medium} / 1.5rem
                ${theme.typography.font.family.lato};
            color: ${theme.colors.tfwBlack99};
            margin: ${theme.spacing.small} ${theme.spacing.medium};
            padding: 0;
        }

        & p,
        h1,
        h2,
        h3,
        h4,
        h5,
        h6 {
            font: ${theme.typography.font.lato.weight.regular} ${theme.typography.font.size.medium} / 1.5rem
                ${theme.typography.font.family.lato};
            color: ${theme.colors.tfwBlack99};
            margin: ${theme.spacing.medium} 0;
            padding: 0;
        }

        & strong {
            color: #4a4a4a;
        }

        & p:first-child {
            margin-top: 0;
        }

        & p:last-child {
            margin-bottom: 0;
        }

        & a {
            color: #016bd4;
            text-decoration: none;

            &:hover {
                color: #0159b8;
                text-decoration: underline;
            }
        }
    `,
)
